/**
 * Görev Yönetimi — Yönetici Paneli (Faz 3)
 *
 * İçerik:
 *  • 4 KPI kartı: toplam, aktif, tamamlandı, geciken + genel tamamlanma oranı
 *  • Kişi bazlı iş yükü tablosu (aktif/tamamlandı/geciken + tamamlanma %)
 *  • En başarılı personel (leaderboard, ilk 10)
 *  • Departman bazlı grafik + tablo
 *  • Öncelik + Durum dağılım grafikleri
 *  • Son 30 gün oluşturma/tamamlanma trend grafiği
 *  • Manuel hatırlatma tarama butonu
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  BarChart3, RefreshCw, Users, CheckCircle2, AlertTriangle, ListTodo,
  Award, TrendingUp, Loader2, ChevronLeft, PieChart as PieIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const STATUS_META = {
  acik: { label: "Açık", color: "#94a3b8" },
  devam: { label: "Devam", color: "#38bdf8" },
  beklemede: { label: "Beklemede", color: "#fbbf24" },
  tamamlandi: { label: "Tamamlandı", color: "#10b981" },
  iptal: { label: "İptal", color: "#64748b" },
};
const PRIORITY_META = {
  dusuk: { label: "Düşük", color: "#94a3b8" },
  normal: { label: "Normal", color: "#38bdf8" },
  yuksek: { label: "Yüksek", color: "#60a5fa" },
  kritik: { label: "Kritik", color: "#ef4444" },
};

export default function TasksAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/tasks/admin/dashboard");
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scanReminders = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/tasks/admin/scan-reminders");
      const total = Object.values(data?.stats || {}).reduce((s, n) => s + Number(n || 0), 0);
      toast.success(`${total} bildirim üretildi`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setScanning(false); }
  };

  const statusData = useMemo(() => {
    if (!data?.status_distribution) return [];
    return Object.entries(data.status_distribution).map(([k, v]) => ({
      name: STATUS_META[k]?.label || k, value: v, color: STATUS_META[k]?.color || "#64748b",
    }));
  }, [data]);
  const priorityData = useMemo(() => {
    if (!data?.priority_distribution) return [];
    return Object.entries(data.priority_distribution).map(([k, v]) => ({
      name: PRIORITY_META[k]?.label || k, value: v, color: PRIORITY_META[k]?.color || "#64748b",
    }));
  }, [data]);
  const trendData = useMemo(() => data?.trend || [], [data]);
  const workload = data?.workload || [];
  const leaderboard = data?.leaderboard || [];
  const departments = data?.departments || [];
  const summary = data?.summary || {};

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Yükleniyor…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8">
        <PageHeader title="Yönetici Paneli" subtitle="Görev Yönetimi Faz 3" icon={BarChart3} />
        <Card className="p-6 bg-rose-950/30 border-rose-900/60 rounded-sm text-rose-200" data-testid="admin-error">
          <AlertTriangle className="w-6 h-6 mb-2" />
          <div className="font-semibold mb-1">Yetkisiz veya hata</div>
          <div className="text-sm">{error}</div>
          <div className="text-xs mt-3 text-rose-300/70">
            Bu sayfa yalnızca Sistem Yöneticisi / Yönetici rolündeki kullanıcılar için erişilebilir.
          </div>
          <Link to="/gorevler" className="inline-flex items-center gap-1 mt-4 text-blue-400 hover:text-blue-300 text-sm">
            <ChevronLeft className="w-4 h-4" /> Görevlere Dön
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="pb-10">
      <PageHeader
        title="Görev Yönetici Paneli"
        subtitle={`Faz 3 · İş yükü, tamamlama oranları, hatırlatma taraması`}
        icon={BarChart3}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/gorevler" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-blue-400 px-3 py-1.5 border border-slate-800 rounded-sm uppercase tracking-widest font-mono">
              <ChevronLeft className="w-3.5 h-3.5" /> Görevler
            </Link>
            <Button
              data-testid="scan-reminders-btn"
              onClick={scanReminders}
              disabled={scanning}
              variant="outline"
              className="rounded-sm text-xs uppercase tracking-widest border-purple-800 text-purple-300 hover:bg-purple-950/30"
            >
              {scanning ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Hatırlatmaları Tara
            </Button>
            <Button
              onClick={load}
              variant="outline"
              className="rounded-sm text-xs uppercase tracking-widest"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Yenile
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-summary">
          <KpiCard label="Toplam Görev" value={summary.total} icon={ListTodo} color="text-slate-100" bg="bg-slate-900/60" />
          <KpiCard label="Aktif" value={summary.active} icon={TrendingUp} color="text-sky-300" bg="bg-sky-950/40" />
          <KpiCard label="Tamamlandı" value={summary.completed} icon={CheckCircle2} color="text-emerald-300" bg="bg-emerald-950/40" />
          <KpiCard label="Geciken" value={summary.overdue} icon={AlertTriangle} color="text-rose-300" bg="bg-rose-950/40" />
          <KpiCard label="Tamamlanma %" value={`${summary.completion_rate ?? 0}%`} icon={Award} color="text-blue-300" bg="bg-blue-950/40" />
        </div>

        {/* Trend chart */}
        <Card className="p-4 rounded-sm bg-card border-border" data-testid="admin-trend">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-bold text-slate-100">Son 30 Gün Aktivite</div>
              <div className="text-[10px] text-slate-500 font-mono uppercase tracking-widest">
                Günlük oluşturma vs tamamlanma
              </div>
            </div>
          </div>
          <div className="h-64">
            {trendData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm">
                Son 30 günde veri yok
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                  <XAxis dataKey="day" tick={{ fill: "#64748b", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 2, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="created" name="Oluşturulan" stroke="#60a5fa" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="completed" name="Tamamlanan" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Distributions */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="p-4 rounded-sm bg-card border-border" data-testid="admin-status-chart">
            <div className="text-sm font-bold text-slate-100 mb-1 flex items-center gap-2">
              <PieIcon className="w-4 h-4 text-sky-400" /> Durum Dağılımı
            </div>
            <div className="h-56">
              {statusData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-500 text-sm">Veri yok</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={{ fill: "#94a3b8", fontSize: 10 }}>
                      {statusData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 2, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
          <Card className="p-4 rounded-sm bg-card border-border" data-testid="admin-priority-chart">
            <div className="text-sm font-bold text-slate-100 mb-1 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-400" /> Öncelik Dağılımı
            </div>
            <div className="h-56">
              {priorityData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-500 text-sm">Veri yok</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={priorityData}>
                    <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 10 }} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 2, fontSize: 12 }} />
                    <Bar dataKey="value" name="Görev">
                      {priorityData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>

        {/* Leaderboard + Workload */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="p-4 rounded-sm bg-card border-border" data-testid="admin-leaderboard">
            <div className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
              <Award className="w-4 h-4 text-yellow-400" /> En Başarılı Personel
            </div>
            {leaderboard.length === 0 ? (
              <div className="text-slate-500 text-sm text-center py-6">Henüz veri yok</div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((row, i) => (
                  <div key={row.email} className="flex items-center gap-3 p-2 rounded-sm bg-slate-900/40" data-testid={`lb-row-${i}`}>
                    <div className={`w-7 h-7 flex items-center justify-center rounded-sm text-xs font-bold ${
                      i === 0 ? "bg-yellow-500/20 text-yellow-300 border border-yellow-800/60" :
                      i === 1 ? "bg-slate-500/20 text-slate-300 border border-slate-700" :
                      i === 2 ? "bg-blue-500/20 text-blue-300 border border-blue-800/60" :
                      "bg-slate-900 text-slate-500 border border-slate-800"
                    }`}>{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-100 font-medium truncate">{row.email}</div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        {row.completed} tamamlandı · %{row.completion_rate} oran
                      </div>
                    </div>
                    <div className="text-[11px] font-mono text-emerald-400">
                      {row.completed}/{row.total}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-4 rounded-sm bg-card border-border" data-testid="admin-departments">
            <div className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-400" /> Departman Tamamlama Oranları
            </div>
            {departments.length === 0 ? (
              <div className="text-slate-500 text-sm text-center py-6">Departman verisi yok</div>
            ) : (
              <div className="space-y-2">
                {departments.map((d) => (
                  <div key={d.department} className="p-2 rounded-sm bg-slate-900/40" data-testid={`dept-${d.department}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs text-slate-100 font-semibold">{d.department}</div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        {d.user_count} kişi · {d.total} görev
                      </div>
                    </div>
                    <div className="relative h-2 bg-slate-800 rounded-sm overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-emerald-400"
                        style={{ width: `${Math.min(100, d.completion_rate)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[10px] mt-1 font-mono">
                      <span className="text-emerald-400">✓ {d.completed}</span>
                      <span className="text-slate-400">{d.active} aktif</span>
                      <span className={d.overdue > 0 ? "text-rose-400" : "text-slate-500"}>
                        ⚠ {d.overdue}
                      </span>
                      <span className="text-slate-300">%{d.completion_rate}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Full Workload Table */}
        <Card className="p-4 rounded-sm bg-card border-border" data-testid="admin-workload">
          <div className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-sky-400" /> Personel İş Yükü ({workload.length})
          </div>
          {workload.length === 0 ? (
            <div className="text-slate-500 text-sm text-center py-6">Henüz görev atanmamış</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                    <th className="px-3 py-2 text-left">Personel</th>
                    <th className="px-3 py-2 text-right">Toplam</th>
                    <th className="px-3 py-2 text-right">Aktif</th>
                    <th className="px-3 py-2 text-right">Tamamlandı</th>
                    <th className="px-3 py-2 text-right">İptal</th>
                    <th className="px-3 py-2 text-right">Geciken</th>
                    <th className="px-3 py-2 text-right">Tamamlanma %</th>
                  </tr>
                </thead>
                <tbody>
                  {workload.map((row) => (
                    <tr key={row.email} className="border-b border-slate-900 hover:bg-slate-900/40" data-testid={`wl-row-${row.email}`}>
                      <td className="px-3 py-2 text-slate-100">{row.email}</td>
                      <td className="px-3 py-2 text-right text-slate-300 font-mono">{row.total}</td>
                      <td className="px-3 py-2 text-right text-sky-300 font-mono">{row.active}</td>
                      <td className="px-3 py-2 text-right text-emerald-300 font-mono">{row.completed}</td>
                      <td className="px-3 py-2 text-right text-slate-500 font-mono">{row.cancelled}</td>
                      <td className={`px-3 py-2 text-right font-mono ${row.overdue > 0 ? "text-rose-400 font-bold" : "text-slate-500"}`}>{row.overdue}</td>
                      <td className="px-3 py-2 text-right text-blue-300 font-mono">%{row.completion_rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, bg }) {
  return (
    <Card className={`p-3 rounded-sm border-slate-800 ${bg}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      </div>
      <div className={`text-xl font-bold ${color}`}>{value ?? 0}</div>
    </Card>
  );
}
